import { DepressionContentPage } from "@/components/DepressionContentPage";

const HelpingSpouseWithDepression = () => (
  <DepressionContentPage
    seo={{
      title:
        "Helping a Spouse With Depression | Neuro Wellness TMS Centers of America",
      description:
        "When your spouse or partner is living with depression, supporting them without becoming their clinician is a delicate balance. Learn about treatment options and how to help.",
      canonical: "/helping-spouse-with-depression",
      keywords:
        "helping spouse with depression, partner depression support, spouse treatment-resistant depression, Coral Springs",
    }}
    badge="For Partners"
    heroTitle="When Your Spouse or Partner Is Living With Depression"
    heroSubtitle="It can feel like watching someone you love disappear into depression. You want to help, but you're not sure how — and you don't want to become their clinician. Here's what can help."
    sections={[
      {
        heading: "Supporting Without Becoming Their Clinician",
        variant: "text",
        body: (
          <p>
            There's a difference between supporting someone and trying to manage
            their treatment. You can listen, encourage, and learn alongside them
            — but medical decisions belong between your partner and their
            clinician. Your role is to be present, not to prescribe.
          </p>
        ),
      },
      {
        heading: "Encouraging Professional Evaluation",
        variant: "text",
        body: (
          <p>
            If your partner hasn't been evaluated, or if their current treatment
            isn't providing enough relief, encouraging a professional evaluation
            is one of the most useful things you can do. You can offer to help
            make the call or attend the consultation with them.
          </p>
        ),
      },
      {
        heading: "Learning About Treatment Options Together",
        variant: "text",
        body: (
          <p>
            When medication or therapy hasn't been enough, other options exist.
            TMS, accelerated TMS, SAINT®, and SPRAVATO® work through different
            mechanisms than oral antidepressants. Learning about these together
            can open a conversation about next steps without pressure.
          </p>
        ),
      },
      {
        heading: "Depression Despite Medication or Therapy",
        variant: "text",
        body: (
          <p>
            If your partner has tried medication or therapy and is still
            struggling, they may be experiencing treatment-resistant depression.
            This is common, and it doesn't mean they've failed. It means the
            standard approach hasn't reached them — and other treatments were
            developed for exactly this situation.
          </p>
        ),
      },
      {
        heading: "What to Say",
        variant: "callout",
        callout:
          "You might say: \"I found a center that offers treatments I hadn't heard of before. I'm not asking you to commit to anything — I just want us to learn about the options together.\" Keep it low-pressure. A no today is not permanent.",
      },
      {
        heading: "Family Consultation",
        variant: "text",
        body: (
          <p>
            You can contact our team to learn about treatment options on behalf
            of your partner. With their consent, you may participate in the
            consultation. The decision to begin treatment belongs to the
            patient, but understanding the landscape is something you can do
            together.
          </p>
        ),
      },
    ]}
    faq={[
      {
        question: "Can I call about treatment for my spouse?",
        answer:
          "Yes. You can contact us to learn general information about our services and the consultation process. Your spouse must consent to and participate in treatment, but gathering information is something you can do now.",
      },
      {
        question: "Can I attend the consultation with my spouse?",
        answer:
          "With your spouse's consent, yes. We encourage family involvement when it's helpful to the patient.",
      },
      {
        question: "What if my spouse doesn't want to talk about treatment?",
        answer:
          "Keep the door open. Share information without pressure. Sometimes the right moment comes later. A no today is not permanent.",
      },
    ]}
    relatedLinks={[
      {
        title: "Helping Someone You Love With Depression",
        href: "/helping-someone-with-depression",
      },
      {
        title: "When Antidepressants Aren't Enough",
        href: "/when-antidepressants-dont-work",
      },
      {
        title: "Depression Treatment Options",
        href: "/depression-treatment-options",
      },
      {
        title: "What Happens During a Consultation",
        href: "/depression-treatment-consultation",
      },
    ]}
    finalCtaHeading="Learn About Options Together"
    finalCtaText="You don't have to navigate this alone. Call our team to understand what treatment options may be available for your partner."
  />
);

export default HelpingSpouseWithDepression;
